import { useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Menu, Select } from 'antd';
import clsx from 'clsx';
import withRouter from 'helpers/withRouter';
import MainStyles from 'assets/styles/main.module.css';
import { ROUTES, STATUS_SUCCESS } from 'const';
import { NOTIFICATION_TEXT_TRANSLATIONS } from 'const/translations';
import { Notification } from 'components/Notification';
import { MobileBlocker } from 'components/MobileBlocker';
import { CartDropdown } from 'components/CartDropdown/CartDropdown';
import { getScreenWidthBreakpointMedia } from 'shared/lib';
import { useSimpleMedia } from 'hooks/useMedia';
import { Logo } from 'components/ui/Logo';
import { getCompaniesRequest } from 'modules/loggedUserInfo/actions';

import HeaderStyles from './MainHeader.module.css';
import { HamburgerButton } from '../ui/HamburgerButton';
import { PROP_TYPE_STATUS } from '../../const/propTypes';
import { LogoutDropdown } from './LogoutDropdown';
import { ModalContainer } from '../../containers/ModalContainer';

const { MOBILE_DISCLAIMER } = NOTIFICATION_TEXT_TRANSLATIONS;

const { Option } = Select;

const propTypes = {
  registeredCompanies: PropTypes.array,
  currentCompany: PropTypes.object,
  handleCompanyChange: PropTypes.func,
  isSideBarOpen: PropTypes.bool,
  isSideBarOpenToggle: PropTypes.func,
  userInfoStatus: PROP_TYPE_STATUS,
  userInfoFullName: PropTypes.string,
  modalProps: PropTypes.object,
  isMainUser: PropTypes.bool,
  userModules: PropTypes.any,
};

const SCREEN_WIDTH_BREAKPOINT_MEDIA = getScreenWidthBreakpointMedia(1300);

const MainHeader = memo(
  ({
    registeredCompanies,
    currentCompany,
    handleCompanyChange,
    isSideBarOpen,
    isSideBarOpenToggle,
    userInfoStatus,
    userInfoFullName,
    modalProps,
    isMainUser,
    userModules,
  }) => {
    const dispatch = useDispatch();
    const isLaptopVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);

    const onSelectSearch = useCallback(
      (value) => {
        const payload = value ? { search: value } : {};
        dispatch(getCompaniesRequest(payload));
      },
      [dispatch]
    );

    const menuItems = [
      {
        key: ROUTES.ROOT,
        className: HeaderStyles.menuItem,
        label: isLaptopVersion ? (
          <>
            <HamburgerButton
              isOpen={isSideBarOpen}
              toggleIsOpen={isSideBarOpenToggle}
            />
            <Logo
              className={clsx(
                HeaderStyles.mainLogo,
                HeaderStyles.mainLogoMobile
              )}
            />
          </>
        ) : (
          <Logo className={HeaderStyles.mainLogo} />
        ),
      },
      {
        key: 'company-select',
        className: clsx(HeaderStyles.menuItem, MainStyles.companySelect),
        label: (
          <Select
            value={
              registeredCompanies.find((c) => c.id === currentCompany.id)
                ? currentCompany.id
                : currentCompany.name
            }
            className={HeaderStyles.coloredDropdown}
            onSelect={handleCompanyChange}
            classNames={{ popup: { root: HeaderStyles.dropdownMenu } }}
            onSearch={onSelectSearch}
            filterOption={false}
            showSearch
            getPopupContainer={() => document.getElementById('dropdown-area')}
            disabled={!userModules}
          >
            {registeredCompanies
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(({ id, name }) => (
                <Option value={id} className={HeaderStyles.option} key={id}>
                  {name}
                </Option>
              ))}
          </Select>
        ),
      },
    ];

    if (!!currentCompany.activated && isMainUser) {
      menuItems.push({
        key: 'cart',
        className: clsx(
          HeaderStyles.menuItem,
          HeaderStyles.profileDropdown,
          HeaderStyles.check
        ),
        label: <CartDropdown />,
      });
    }

    if (userInfoStatus === STATUS_SUCCESS) {
      menuItems.push({
        key: 'logout',
        className: clsx(
          HeaderStyles.menuItem,
          HeaderStyles.profileDropdown,
          HeaderStyles.userMenu
        ),
        label: (
          <LogoutDropdown
            userInfoFullName={userInfoFullName}
            isCurrentCompanyActivated={!!currentCompany.activated}
            isMainUser={isMainUser}
          />
        ),
      });
    }

    return (
      <header className={MainStyles.header}>
        <ModalContainer {...modalProps} />
        <Menu
          mode="horizontal"
          role="navigation"
          className={clsx(MainStyles.headerMenu, MainStyles.container)}
          items={menuItems}
        />
        <MobileBlocker
          mobile={
            <Notification
              message={MOBILE_DISCLAIMER}
              id="mobile-disclaimer"
              headerFix
            />
          }
          desktop={null}
        />
      </header>
    );
  }
);

MainHeader.propTypes = propTypes;

export const MainHeaderWithRouter = withRouter(MainHeader);
